/**
 * New case wizard transport (#758). The pickers are reads (the local ticket mirror and Conduit's search); the one
 * write freezes a `create_case` card, and the ordinary card confirm runs it. A failed lookup throws; it is never shown
 * as an empty list (hard rule 10).
 */
import { queryOptions } from '@tanstack/react-query'
import { unwrap } from '../../lib/api/client'
import {
  type CardOut,
  type ContactOptionsOut,
  type CustomerOptionsOut,
  confirmApiV1CardsCardIdConfirmPost,
  contactsApiV1NewCaseContactsGet,
  customersApiV1NewCaseCustomersGet,
  dismissApiV1CardsCardIdDismissPost,
  type NewCaseIn,
  ownersApiV1OwnersGet,
  proposeApiV1NewCasePost,
} from '../../lib/api/generated'

export type { CardOut, ContactOptionsOut, CustomerOptionsOut, NewCaseIn }
export type Channel = NonNullable<NewCaseIn['channel']>

export const CHANNELS: { value: Channel; label: string; hint: string }[] = [
  { value: 'email_out', label: 'We email them', hint: 'We are reaching out' },
  { value: 'called', label: 'They called', hint: 'Log what was said' },
  { value: 'internal', label: 'Internal', hint: 'Proactive or internal work' },
]

export const ownersQuery = () =>
  queryOptions({ queryKey: ['new-case', 'owners'], queryFn: () => unwrap(ownersApiV1OwnersGet()), staleTime: 60_000, retry: false })

export const newCaseKeys = {
  customers: (q: string) => ['new-case', 'customers', q] as const,
  contacts: (accountId: string | null, q: string) => ['new-case', 'contacts', accountId, q] as const,
}

export const customersQuery = (q: string) =>
  queryOptions({
    queryKey: newCaseKeys.customers(q),
    queryFn: () => unwrap(customersApiV1NewCaseCustomersGet({ query: { q } })),
    enabled: q.length >= 2,
    staleTime: 30_000,
    retry: false,
  })

export const contactsQuery = (accountId: string | null, q: string, enabled: boolean) =>
  queryOptions({
    queryKey: newCaseKeys.contacts(accountId, q),
    queryFn: () => unwrap(contactsApiV1NewCaseContactsGet({ query: { account_id: accountId, q } })),
    enabled,
    staleTime: 30_000,
    retry: false,
  })

export function proposeNewCase(body: NewCaseIn): Promise<CardOut> {
  return unwrap(proposeApiV1NewCasePost({ body }))
}

/** The next move the wizard pre-fills (mirrors the server's `default_next_move`); the person can change it. */
export function defaultNextMove(channel: Channel, sendNow: boolean, contactName: string): { text: string; owner: 'us' | 'customer' } {
  const who = contactName.trim() || 'the customer'
  if (channel === 'email_out' && sendNow) return { text: `Waiting for ${who} to reply`, owner: 'customer' }
  if (channel === 'called') return { text: `Call ${who} back`, owner: 'us' }
  return { text: 'Follow up', owner: 'us' }
}

/** `YYYY-MM-DD` `days` from now in local time, the format of a date input. */
export function dueInDays(days: number, from: Date = new Date()): string {
  const d = new Date(from.getFullYear(), from.getMonth(), from.getDate() + days)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export function confirmNewCaseCard(cardId: string): Promise<CardOut> {
  return unwrap(confirmApiV1CardsCardIdConfirmPost({ path: { card_id: cardId }, body: { acknowledged_warnings: [] } }))
}

export function dismissNewCaseCard(cardId: string, reason: string | null = 'edited before confirming'): Promise<CardOut> {
  return unwrap(dismissApiV1CardsCardIdDismissPost({ path: { card_id: cardId }, body: { reason } }))
}
